import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import B2BLayout from '../../../layouts/b2b'
import { Form, message, Modal, Space } from 'antd'
import { getColumns } from './columns'
import { createSelector } from 'reselect'
import { useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { objectToUrl } from '../../utils'
import FiltersList from '../../../components/v2/filters'
import { State } from '../../../interfaces'
import fetchAPI from '../../../../lib/utils/fetch-api'
import {
  getJsonSettingsByKey,
  TableSettingsButton,
  TransferItem,
  getColumnOrder,
} from '../../../components/v2/table-settings-button'
import { getPrintPath } from '../../../components/v2/export-excel-button'
import { CustomersPrintButton } from '../ui/customers-print-button/customers-print-button'
import DataTable from '../../../components/data-table'
import StickyToolbar from '../../../layouts/b2b/sticky-toolbar'
import FiltersDrawer, { FiltersDrawerRef } from '../../../components/v2/filters-drawer'
import { useMediaQuery } from 'react-responsive'
import { useHeaderActions } from '../../../contexts/header-actions-context'
import { HeaderAction } from '../../../contexts/header-actions-context/interface'
import { FilterOutlined } from '@ant-design/icons'
import { getFilters as buildFilters } from './filters'
import { getMobileFields } from './mobile-fields'
import { hasPermissionScope } from '../../../lib/permissions'
import { Permissions } from '../../../constants/permissions'
import { useDemoPrivacy } from '../../../contexts/demo-privacy-context'

const createSession = createSelector(
  (state: State) => state.session,
  sessionData => sessionData,
)

type TablePaginationPosition = 'bottomCenter'

interface IFilterProps {
  phone: null | string
  manager: null | number
  id: null | number
  organization: null | number
  office: null | number
  stage: null | string
  dates: null | string
  fio: null | string
  brand: null | number
  production_number: null | string
  additional: null | string
}

const CustomersList = () => {
  const [filtersForm] = Form.useForm()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const [data, setData] = useState([])
  const [availableFilters, setAvailableFilters] = useState({
    organizations: [],
    offices: [],
    brands: [],
  })
  const [urlParams] = useState(Object.fromEntries(new URLSearchParams(search)))
  const [currentFilters, setCurrentFilters] = useState<IFilterProps>({
    phone: urlParams?.phone,
    manager: urlParams.manager ? +urlParams.manager : null,
    id: urlParams.id ? +urlParams.id : null,
    organization: urlParams.organization ? +urlParams.organization : null,
    office: urlParams.office ? +urlParams.office : null,
    stage: urlParams?.stage,
    dates: urlParams?.dates,
    fio: urlParams?.fio,
    brand: urlParams?.brand ? +urlParams.brand : null,
    production_number: urlParams?.production_number || null,
    additional: urlParams?.additional || null,
  })
  const { sessionData } = useSelector(createSession)
  const { hidePhones } = useDemoPrivacy()
  const isGoLeaderByScope = hasPermissionScope(
    sessionData?.permissionsWithScope,
    Permissions.ACCESS_ORDERS,
    ['organization', 'all'],
  )
  const isBrandLeaderByScope = hasPermissionScope(
    sessionData?.permissionsWithScope,
    Permissions.ACCESS_ORDERS,
    ['brand'],
  )
  // Temporary fallback while other modules still use role-based access checks.
  const isGoLeader = isGoLeaderByScope
  const isBrandLeader = !isGoLeader && isBrandLeaderByScope
  const [loading, setLoading] = useState(false)
  const [bottomCenter] = useState<TablePaginationPosition>('bottomCenter')
  const [sorter, setSorter] = useState<any>({})

  const jsonSettings = useSelector(state => getJsonSettingsByKey(state, pathname))
  const headerRef = useRef<HTMLDivElement | null>(null)
  const filtersDrawerRef = useRef<FiltersDrawerRef | null>(null)
  const tableSettingsRef = useRef<{ open: () => void } | null>(null)
  const { setHeaderActions } = useHeaderActions()
  const isMobile = useMediaQuery({ maxWidth: 768 })

  const [pagination, setPagination] = useState<any>({
    current: Number(urlParams?.page) || 1,
    pageSize: 50,
    position: [bottomCenter],
    showSizeChanger: true,
    total: 0,
  })

  /**
   * Хлебные крошки
   */
  const breadcrumbs = useMemo(
    () => [
      {
        href: location.pathname,
        title: 'Клиенты',
      },
    ],
    [location.pathname],
  )

  useEffect(() => {
    getAvailableFilters()
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(search)
    const nextFilters: IFilterProps = {
      phone: params.get('phone') || null,
      manager: params.get('manager') ? +params.get('manager')! : null,
      id: params.get('id') ? +params.get('id')! : null,
      organization: params.get('organization') ? +params.get('organization')! : null,
      office: params.get('office') ? +params.get('office')! : null,
      stage: params.get('stage') || null,
      dates: params.get('dates') || null,
      fio: params.get('fio') || null,
      brand: params.get('brand') ? +params.get('brand')! : null,
      production_number: params.get('production_number') || null,
      additional: params.get('additional') || null,
    }
    setCurrentFilters(nextFilters)
  }, [search])

  useEffect(() => {
    getData()
  }, [search])

  const filters = useMemo(
    () =>
      buildFilters({
        currentFilters,
        availableFilters,
        isGoLeader,
        isBrandLeader,
      }),
    [currentFilters, availableFilters, isGoLeader, isBrandLeader],
  )

  /**
   * Получает список доступных фильтров для обращений
   */
  const getAvailableFilters = useCallback(() => {
    const enumsList = { types: ['organizations', 'brands', 'offices'] }
    if (isBrandLeader) {
      fetchAPI(`/api/get-offices?brand_id=${sessionData?.organization?.brand}`)
        .then(enums => {
          interface IOfficeResponse {
            id: number
            name: string
          }
          const filteredEnums = {
            brands: [],
            organizations: [],
            offices: enums.map((i: IOfficeResponse) => ({ value: i.id, label: i.name })),
          }
          setAvailableFilters(filteredEnums)
        })
        .catch(error => {
          console.error('Произошла ошибка при загрузке данных:', error)
        })
    } else {
      fetchAPI(`/api/enum-list${objectToUrl(enumsList)}`)
        .then(enums => {
          interface IEnumResponse {
            id: number
            value: string
          }
          const filteredEnums = {
            organizations: enums.organizations.map((i: IEnumResponse) => ({
              value: i.id,
              label: i.value,
            })),
            offices: enums.offices.map((i: IEnumResponse) => ({ value: i.id, label: i.value })),
            brands: enums.brands.map((i: IEnumResponse) => ({ value: i.id, label: i.value })),
          }
          setAvailableFilters(filteredEnums)
        })
        .catch(error => {
          console.error('Произошла ошибка при загрузке данных:', error)
        })
    }
  }, [isBrandLeader, sessionData])

  /**
   * Получает записи
   */
  const getData = useCallback(async () => {
    setLoading(true)
    await fetchAPI(`/api/customers/get-list/${search}`)
      .then((response: Record<string, any>) => {
        const { data, pagination, sorter } = response
        if (data) {
          setPagination(prevState => ({
            ...prevState,
            ...pagination,
          }))
          setSorter(sorter)
          setData(data)
        }
        setLoading(false)
      })
      .catch(error => {
        console.error('Произошла ошибка при загрузке данных:', error)
      })
  }, [search])

  /**
   * Отслеживает изменение состояния таблицы
   */
  const handleTableChange = (paginationArg, _filters, sorterArg) => {
    const currentUrlParams = Object.fromEntries(new URLSearchParams(search))
    setPagination(prev => ({ ...prev, ...paginationArg }))
    let current = {
      ...currentUrlParams,
      sort_order: sorterArg?.order,
      sort_field: sorterArg?.field,
      page: paginationArg.current,
      pageSize: paginationArg.pageSize,
    }
    if (current?.page === 1) {
      delete current.page
    }
    if (current?.sort_order === undefined) {
      delete current.sort_order
      delete current.sort_field
    }
    const urlParams = new URLSearchParams(current).toString()
    navigate({ search: urlParams })
  }

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination(prevState => ({
      ...prevState,
      current: Number(page) || 1,
      pageSize,
    }))
    const currentUrlParams = new URLSearchParams(search)
    currentUrlParams.set('page', page.toString())
    currentUrlParams.set('pageSize', pageSize.toString())
    navigate({ search: currentUrlParams.toString() })
  }

  /**
   * Отправляет на страницу деталки
   * @param record
   */
  const handleRowClick = record => {
    const { id } = record
    navigate(`/client-new/${id}`)
  }

  /**
   * Отслеживает состояние фильтров
   */
  const handleFilterChange = useCallback(
    (filters: any) => {
      const obj = { ...filters }
      /* в строку попадают только непустые строковые и положительные числовые значения */
      const newParams = Object.keys(obj).reduce((acc, key) => {
        if (
          (typeof obj[key] === 'string' && obj[key].length) ||
          (typeof obj[key] === 'number' && obj[key] > 0)
        ) {
          acc[key] = obj[key]
        }
        return acc
      }, {})

      // Сохраняем pageSize
      if (pagination.pageSize) {
        newParams['pageSize'] = pagination.pageSize
      }

      const newUrlParams = new URLSearchParams(newParams).toString()
      navigate({ search: newUrlParams })
    },
    [pagination.pageSize],
  )

  const deleteRecord = useCallback(
    async (id: number, deleteAll?: boolean) => {
      try {
        const response = await fetchAPI(`/api/customer`, {
          method: 'DELETE',
          body: JSON.stringify({ id, delete_all: deleteAll }),
        })
        const hasCards = response?.warning

        const makeUrls = (ids, url) => {
          return (
            <>
              {ids.map((id, index) => (
                <>
                  <a href={`/${url}/${id}`} target={'_blank'}>
                    №{id}
                  </a>
                  {index < ids.length - 1 && ', '}
                </>
              ))}
            </>
          )
        }

        if (hasCards) {
          const content = (
            <>
              У клиента усть:
              <br />
              {Boolean(hasCards?.appeal?.length) && (
                <>
                  Обращения: {makeUrls(hasCards.appeal, 'appeal')}
                  <br />
                </>
              )}
              {Boolean(hasCards?.order?.length) && (
                <>
                  Заказы: {makeUrls(hasCards.order, 'order')}
                  <br />
                </>
              )}
              {Boolean(hasCards?.measurement?.length) && (
                <>
                  Замеры: {makeUrls(hasCards.measurement, 'measurement')}
                  <br />
                </>
              )}
              {Boolean(hasCards?.montage?.length) && (
                <>Монтажи: {makeUrls(hasCards.montage, 'montage')}</>
              )}
            </>
          )
          Modal.confirm({
            title: 'Внимание!',
            content,
            okText: 'Удалить все',
            onOk: () => {
              deleteRecord(id, true)
            },
          })
          return
        } else if (response?.success) {
          message.success('Пользователь успешно удален')
          await fetchAPI(`/api/customers/get-list/${search}`)
            .then((response: Record<string, any>) => {
              const { data, pagination: paginationData, sorter: sorterData } = response
              if (data) {
                setPagination(prevState => ({
                  ...prevState,
                  ...paginationData,
                }))
                setSorter(sorterData)
                setData(data)
              }
            })
            .catch(error => {
              console.error('Произошла ошибка при загрузке данных:', error)
            })
        } else {
          message.error(response?.message || 'Не удалось удалить пользователя')
        }
      } catch (error) {
        console.error('Ошибка при удалении пользователя:', error)
        message.error('Произошла ошибка при удалении пользователя')
      }
    },
    [search],
  )

  const columns = useMemo(() => getColumns(isGoLeader, isBrandLeader, sorter, deleteRecord), [
    isBrandLeader,
    isGoLeader,
    sorter,
    deleteRecord,
    hidePhones,
  ])

  const viewColumns = useMemo(() => {
    const columnOrder = getColumnOrder(jsonSettings)

    if (columnOrder.length > 0) {
      let result: any[] = []
      for (let i = 0; i < columnOrder.length; i++) {
        let item = columns.find((column: any) => columnOrder[i] === column.dataIndex)
        if (item) {
          result.push(item)
        }
      }
      const actionColumn = columns.find((column: any) => column.key === 'operation')
      if (actionColumn && !result.find((col: any) => col?.key === 'operation')) {
        result.push(actionColumn)
      }
      return result
    }
    return columns
  }, [columns, jsonSettings])

  const excelPath = useMemo(() => {
    return getPrintPath('/api/customers/get-list', new URLSearchParams(search))
  }, [search])

  const settingsColumns = useMemo(
    () =>
      columns
        .filter((column: any) => column?.key !== 'operation')
        .map((column: any) => ({
          key: column?.dataIndex,
          title: column?.title,
        })),
    [columns],
  ) as TransferItem[]

  const mobileFields = useMemo(() => getMobileFields(), [hidePhones])

  useEffect(() => {
    const actions: HeaderAction[] = []
    if (filtersDrawerRef) {
      actions.push({
        key: 'filters',
        icon: <FilterOutlined />,
        onClick: () => {
          filtersDrawerRef.current?.toggle()
        },
        tooltip: 'Фильтры',
      })
    }
    setHeaderActions(actions)
    return () => setHeaderActions([])
  }, [setHeaderActions])

  return (
    <B2BLayout breadcrumbs={breadcrumbs} isViewScrollButton pageTitle={'Клиенты'}>
      <StickyToolbar
        ref={headerRef}
        collapsible={
          <Space style={{ width: '100%', justifyContent: 'space-between' }} align={'start'} wrap>
            <Space align={'start'} wrap>
              <FiltersList
                filters={filters
                  .filter(f => ['id', 'fio', 'phone'].includes(f.name))
                  .sort((a, b) => (a.name === 'id' ? -1 : b.name === 'id' ? 1 : 0))}
                onChange={handleFilterChange}
                form={filtersForm}
              />
            </Space>
            <FiltersDrawer
              filters={filters}
              onChange={handleFilterChange}
              currentFilters={currentFilters as any}
              ref={filtersDrawerRef}
            />
          </Space>
        }
        persistent={
          !isMobile ? (
            <Space style={{ width: '100%', justifyContent: 'space-between' }} align={'center'} wrap>
              <div />
              <Space align={'center'} wrap>
                <CustomersPrintButton path={excelPath} columns={viewColumns} />
                <TableSettingsButton items={settingsColumns} ref={tableSettingsRef} />
              </Space>
            </Space>
          ) : undefined
        }
      />
      <DataTable
        columns={viewColumns as any}
        mobileFields={mobileFields}
        data={data}
        loading={loading}
        rowKey={r => String(r.id)}
        highlightLastRowPrefix={'client'}
        pagination={pagination}
        onRowClick={handleRowClick}
        onPageChange={handlePageChange}
        onTableChange={handleTableChange}
        headerRef={headerRef}
        bordered
        scroll={{ x: 'max-content' }}
        maxLines={4}
      />
    </B2BLayout>
  )
}

export default CustomersList
